import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { login } from '../../redux/authSlice';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

const LoginComponent = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Efecto para el temporizador de bloqueo
  useEffect(() => {
    let timer;
    if (isLocked && lockTime > 0) {
      timer = setInterval(() => {
        setLockTime(prev => prev - 1);
      }, 1000);
    } else if (lockTime === 0 && isLocked) {
      setIsLocked(false);
    }
    return () => clearInterval(timer);
  }, [isLocked, lockTime]);

  const validateEmail = () => {
    if (!email) {
      setErrors((prev) => ({ ...prev, email: 'El email es requerido.' }));
      return false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors((prev) => ({ ...prev, email: 'El email no es válido.' }));
      return false;
    }
    setErrors((prev) => ({ ...prev, email: null }));
    return true;
  };

  const validatePassword = () => {
    if (!password) {
      setErrors((prev) => ({ ...prev, password: 'La contraseña es requerida.' }));
      return false;
    }
    setErrors((prev) => ({ ...prev, password: null }));
    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (isLocked) {
      setAlert({
        type: 'danger',
        message: `Demasiados intentos fallidos. Por favor, espere ${lockTime} segundos antes de intentar nuevamente.`
      });
      return;
    }

    const isEmailValid = validateEmail();
    const isPasswordValid = validatePassword();

    if (!isEmailValid || !isPasswordValid) {
      setAlert({ type: 'danger', message: 'Por favor, corrige los errores en el formulario.' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });

      const {
        token,
        rol,
        nombre,
        apellido,
        cambio_clave = false,
        solicitar_email_personal = false,
        completar_emergencia = false
      } = response.data;

      dispatch(login({
        token,
        rol,
        nombre,
        apellido,
        cambio_clave,
        solicitar_email_personal,
        completar_emergencia
      }));

      // Resetear intentos después de un login exitoso
      setAttempts(0);

      if (cambio_clave || solicitar_email_personal || completar_emergencia) {
        navigate('/cambio-clave');
      } else {
        navigate('/');
      }

    } catch (error) {
      console.error('Error en login:', error);

      // Incrementar intentos fallidos
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      // Bloquear después de 3 intentos fallidos
      if (newAttempts >= 3) {
        const lockDuration = 30; // 30 segundos de bloqueo
        setIsLocked(true);
        setLockTime(lockDuration);
        setAlert({
          type: 'danger',
          message: `Demasiados intentos fallidos. Por favor, espere ${lockDuration} segundos antes de intentar nuevamente.`
        });
        return;
      }

      // Manejar diferentes tipos de errores
      let errorMessage = 'Error al iniciar sesión. Por favor, verifique sus credenciales.';

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Credenciales inválidas. Por favor, verifique su email y contraseña.';
            break;
          case 401:
            errorMessage = 'No autorizado. Credenciales incorrectas.';
            break;
          case 403:
            errorMessage = 'Acceso denegado. Su cuenta puede estar deshabilitada.';
            break;
          case 429:
            errorMessage = 'Demasiadas solicitudes. Por favor, espere antes de intentar nuevamente.';
            break;
          case 500:
            errorMessage = 'Error del servidor. Por favor, intente más tarde.';
            break;
          default:
            errorMessage = 'Error al conectar con el servidor.';
        }
      } else if (error.request) {
        errorMessage = 'No se pudo conectar con el servidor. Verifique su conexión a internet.';
      }

      setAlert({ type: 'danger', message: errorMessage });

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {alert && (
        <Alert
          variant={alert.type}
          onClose={() => setAlert(null)}
          dismissible
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {alert.message}
        </Alert>
      )}

      <Form onSubmit={handleLogin} noValidate aria-label="Formulario de inicio de sesión">
        <Form.Group controlId="formEmail" className="mb-3">
          <Form.Label htmlFor="emailInput">Correo Electrónico</Form.Label>
          <Form.Control
            id="emailInput"
            type="email"
            placeholder="Ingresa tu correo"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={validateEmail}
            isInvalid={!!errors.email}
            required
            disabled={isLocked}
            aria-label="Correo electrónico"
            aria-required="true"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "emailError" : undefined}
          />
          <Form.Control.Feedback type="invalid" id="emailError">
            {errors.email}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group controlId="formPassword" className="mb-3">
          <Form.Label htmlFor="passwordInput">Contraseña</Form.Label>
          <InputGroup>
            <Form.Control
              id="passwordInput"
              type={showPassword ? "text" : "password"}
              placeholder="Ingresa tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={validatePassword}
              isInvalid={!!errors.password}
              required
              disabled={isLocked}
              aria-label="Contraseña"
              aria-required="true"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? "passwordError" : undefined}
            />
            <InputGroup.Text
              style={{ cursor: 'pointer' }}
              onClick={() => !isLocked && setShowPassword(!showPassword)}
              disabled={isLocked}
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              tabIndex={!isLocked ? "0" : "-1"}
              role="button"
            >
              {showPassword ? <EyeSlash aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </InputGroup.Text>
            <Form.Control.Feedback type="invalid" id="passwordError">
              {errors.password}
            </Form.Control.Feedback>
          </InputGroup>
        </Form.Group>

        <Button
          type="submit"
          variant="primary"
          disabled={isLoading || isLocked}
          className="w-100"
          aria-label={isLocked ? `Inicio de sesión bloqueado. Disponible en ${lockTime} segundos` : "Iniciar sesión"}
          aria-busy={isLoading}
        >
          {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          {isLocked && ` (Disponible en ${lockTime}s)`}
        </Button>
      </Form>
    </>
  );
};

export default LoginComponent;